import { Alert } from '@utils/alert.ts';
import Swal from 'sweetalert2';

const AlertExample = () => {
    // 성공 알림
    const handleSuccess = () => {
        Alert.success('성공!', '작업이 완료되었습니다.');
    };

    // 에러 알림
    const handleError = () => {
        Alert.error('오류 발생', '문제가 발생했습니다. 다시 시도해주세요.');
    };

    // 경고 알림
    const handleWarning = () => {
        Alert.warning('주의', '이 작업은 되돌릴 수 없습니다.');
    };

    // 정보 알림
    const handleInfo = () => {
        Alert.info('정보', '새로운 업데이트가 있습니다.');
    };

    // 확인 다이얼로그
    const handleConfirm = async () => {
        const result = await Alert.confirm(
            '확인하시겠습니까?',
            '이 작업을 진행하시겠습니까?',
        );

        if (result.isConfirmed) {
            Alert.success('확인됨', '작업이 진행됩니다.');
        }
    };

    // 삭제 확인
    const handleDelete = async () => {
        const result = await Alert.delete(
            '삭제하시겠습니까?',
            '이 항목을 삭제하면 복구할 수 없습니다.',
        );

        if (result.isConfirmed) {
            Alert.success('삭제됨', '항목이 삭제되었습니다.');
        }
    };

    // 입력 다이얼로그
    const handleInput = async () => {
        const result = await Alert.input(
            '이름을 입력하세요',
            'text',
            '이름을 입력하세요...',
        );

        if (result.isConfirmed && result.value) {
            Alert.success('입력됨', `안녕하세요, ${result.value}님!`);
        }
    };

    // 이메일 입력 다이얼로그
    const handleEmailInput = async () => {
        const result = await Alert.input(
            '이메일을 입력하세요',
            'email',
            'example@example.com',
        );

        if (result.isConfirmed && result.value) {
            Alert.success('입력됨', `이메일: ${result.value}`);
        }
    };

    // 비밀번호 입력 다이얼로그
    const handlePasswordInput = async () => {
        const result = await Alert.input(
            '비밀번호를 입력하세요',
            'password',
            '비밀번호를 입력하세요...',
        );

        if (result.isConfirmed && result.value) {
            Alert.success('입력됨', '비밀번호가 입력되었습니다.');
        }
    };

    // 텍스트 영역 입력 다이얼로그
    const handleTextareaInput = async () => {
        const result = await Alert.input(
            '메시지를 입력하세요',
            'textarea',
            '여러 줄의 메시지를 입력하세요...',
        );

        if (result.isConfirmed && result.value) {
            Alert.success('입력됨', `메시지: ${result.value}`);
        }
    };

    // 로딩 다이얼로그
    const handleLoading = async () => {
        Alert.loading('데이터 로딩 중...', '잠시만 기다려주세요.');

        // 3초 후 로딩 종료
        setTimeout(() => {
            Alert.close();
            Alert.success('완료', '데이터 로딩이 완료되었습니다.');
        }, 3000);
    };

    // 토스트 알림들
    const handleToastSuccess = () => {
        Alert.toast('성공적으로 저장되었습니다!', 'success');
    };

    const handleToastError = () => {
        Alert.toast('오류가 발생했습니다!', 'error');
    };

    const handleToastWarning = () => {
        Alert.toast('주의하세요!', 'warning');
    };

    const handleToastInfo = () => {
        Alert.toast('알림입니다!', 'info');
    };

    // 커스텀 알림
    const handleCustom = () => {
        Alert.custom({
            title: '커스텀 알림',
            html: `
        <div style="text-align: center;">
          <p>이것은 커스텀 HTML 내용입니다.</p>
          <img src="https://via.placeholder.com/100" alt="placeholder" style="margin: 10px 0;" />
          <p><strong>원하는 HTML을 사용할 수 있습니다!</strong></p>
        </div>
      `,
            imageUrl: 'https://via.placeholder.com/200',
            imageWidth: 200,
            imageHeight: 200,
            showCloseButton: true,
            focusConfirm: false,
            confirmButtonText: '멋져요!',
            confirmButtonColor: '#ff6b6b',
        });
    };

    // 진행률 표시 알림
    const handleProgress = () => {
        let timerInterval: NodeJS.Timeout;
        Alert.custom({
            title: '자동 닫기 알림',
            html: '이 알림은 <b></b> 초 후에 자동으로 닫힙니다.',
            timer: 5000,
            timerProgressBar: true,
            didOpen: () => {
                Swal.showLoading();
                const timer = Swal.getPopup()?.querySelector('b');
                if (timer) {
                    timerInterval = setInterval(() => {
                        timer.textContent = `${Swal.getTimerLeft()}`;
                    }, 100);
                }
            },
            willClose: () => {
                clearInterval(timerInterval);
            },
        }).then(result => {
            if (result.dismiss === Swal.DismissReason.timer) {
                Alert.info('완료', '자동으로 닫혔습니다!');
            }
        });
    };

    // 단계별 알림 (연속 알림)
    const handleSteps = async () => {
        const steps = ['첫 번째 단계', '두 번째 단계', '세 번째 단계'];
        const Queue = Swal.mixin({
            progressSteps: ['1', '2', '3'],
            showCancelButton: true,
            confirmButtonText: '다음 >',
            cancelButtonText: '취소',
        });

        const values: string[] = [];

        for (let i = 0; i < steps.length; i++) {
            const result = await Queue.fire({
                title: steps[i],
                text: `단계 ${i + 1}/${steps.length}`,
                currentProgressStep: i,
                confirmButtonText: i === steps.length - 1 ? '완료' : '다음 >',
            });

            if (result.value) {
                values.push(result.value);
            }

            if (result.dismiss === Swal.DismissReason.cancel) {
                break;
            }

            if (i === steps.length - 1) {
                Alert.success('완료', '모든 단계가 완료되었습니다!');
            }
        }
    };

    return (
        <div
            style={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
            }}
        >
            <h2>SweetAlert2 사용 예시</h2>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={handleSuccess}>성공 알림</button>
                <button onClick={handleError}>에러 알림</button>
                <button onClick={handleWarning}>경고 알림</button>
                <button onClick={handleInfo}>정보 알림</button>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={handleInput}>이름 입력</button>
                <button onClick={handleEmailInput}>이메일 입력</button>
                <button onClick={handlePasswordInput}>비밀번호 입력</button>
                <button onClick={handleTextareaInput}>메시지 입력</button>
                <button onClick={handleLoading}>로딩 다이얼로그</button>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={handleToastSuccess}>토스트 성공</button>
                <button onClick={handleToastError}>토스트 에러</button>
                <button onClick={handleToastWarning}>토스트 경고</button>
                <button onClick={handleToastInfo}>토스트 정보</button>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={handleConfirm}>확인 다이얼로그</button>
                <button onClick={handleDelete}>삭제 확인</button>
                <button onClick={handleCustom}>커스텀 알림</button>
                <button onClick={handleProgress}>진행률 알림</button>
                <button onClick={handleSteps}>단계별 알림</button>
            </div>
        </div>
    );
};

export default AlertExample;
